import React, { useContext, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Box, Container, Grid, Paper, LinearProgress, Typography } from "@mui/material";
import bkstApi from "../../common/api";
import ConfigContext from "../../common/context/ConfigContext";
import { STORAGE_BUCKET } from "../../common/constants";

function ReceiptLineItems({ items }) {
  return items.map((item, idx) => (
    <Grid container spacing={2} key={`${item.name}-${idx}`}>
      <Grid item xs={8}>
        <Typography variant="body1">{item.name}</Typography>
      </Grid>
      <Grid item xs={4} textAlign={"right"}>
        <Typography variant="body1">{item.value}</Typography>
      </Grid>
    </Grid>
  ));
}

function isCurrencyValue(value) {
  return typeof value === "string" && /^\$[\d,]+\.\d{2}$/.test(value.trim());
}

function normalizePaymentLines(items) {
  const normalized = [];

  for (const item of items) {
    if (!item.name && !item.value) {
      continue;
    }

    if (item.name === "Amount" && isCurrencyValue(item.value)) {
      const prev = normalized[normalized.length - 1];
      if (prev && !isCurrencyValue(prev.value)) {
        if (prev.value) {
          prev.subtext = prev.value;
        }
        prev.value = item.value;
        continue;
      }
    }

    normalized.push({ ...item });
  }

  return normalized;
}

function ReceiptPaymentLines({ items }) {
  return normalizePaymentLines(items).map((item, idx) => {
    const key = `${item.name}-${idx}`;

    if (isCurrencyValue(item.value)) {
      return (
        <Grid container spacing={2} key={key}>
          <Grid item xs={8}>
            <Typography variant="body1">{item.name}</Typography>
            {item.subtext && (
              <Typography variant="body2" color="text.secondary">
                {item.subtext}
              </Typography>
            )}
          </Grid>
          <Grid item xs={4} textAlign={"right"}>
            <Typography variant="body1">{item.value}</Typography>
          </Grid>
        </Grid>
      );
    }

    if (!item.value) {
      return (
        <Typography variant="body2" color="text.secondary" key={key}>
          {item.name}
        </Typography>
      );
    }

    return (
      <Typography variant="body2" color="text.secondary" key={key}>
        {item.name} {item.value}
      </Typography>
    );
  });
}

export default function PaymentReceipt() {
  const siteConfig = useContext(ConfigContext);
  let { orderId } = useParams();

  const [receipt, setReceipt] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    bkstApi(`/site/${siteConfig.id}/receipt/${orderId}`).then((res) => {
      setReceipt(res.data.receipt);
      setLoading(false);
    });
  }, [orderId]);

  if (loading) {
    return <LinearProgress />;
  }

  const balance = receipt.balance || [];
  const paymentLines = (receipt.charges || []).filter((item) => item.name || item.value);

  return (
    <Container maxWidth="sm">
      <Box my={6}>
        <Paper elevation={0}>
          <Box p={2}>
            <Box mb={4} textAlign={"center"}>
              <img
                alt={`${siteConfig.biz} Logo`}
                style={{ width: "50px", height: "50px", borderRadius: "5px" }}
                src={`${STORAGE_BUCKET}/img/b/${siteConfig.id}/logo400.webp`}
              />
              <Typography variant="body1" gutterBottom>
                {siteConfig.biz}
              </Typography>
              <Typography variant="body2">{receipt.store.address.street}</Typography>
              <Typography variant="body2">
                {receipt.store.address.city}, {receipt.store.address.state}{" "}
                {receipt.store.address.zip}
              </Typography>
            </Box>
            <Box py={2} sx={{ borderTop: "1px dashed #DEDEDE" }}>
              <ReceiptLineItems items={receipt.items} />
            </Box>
            <Box py={2} sx={{ borderTop: "1px dashed #DEDEDE" }}>
              <ReceiptLineItems items={receipt.payment} />
            </Box>
            {balance.length > 0 && (
              <Box py={2} sx={{ borderTop: "1px dashed #DEDEDE" }}>
                <ReceiptLineItems items={balance} />
              </Box>
            )}
            {paymentLines.length > 0 && (
              <Box
                py={2}
                sx={{ borderTop: "1px dashed #DEDEDE", borderBottom: "1px dashed #DEDEDE" }}
              >
                <Typography
                  variant="overline"
                  color="textSecondary"
                  display="block"
                  sx={{ mb: 1.5 }}
                >
                  Payments
                </Typography>
                <ReceiptPaymentLines items={paymentLines} />
              </Box>
            )}
            <Box pt={2} textAlign={"center"}>
              <Typography variant="body2">{receipt.footer}</Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}
